import React from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
	ApLoaderDefault,
	ApLoaderText,
	ApButtonSecondary,
} from '@alixpartners/ui-components';
import { PERMISSIONS, PermissionsContext } from 'contexts/PermissionsContext';
import ContextualMenu from 'Details/ContextualMenu';
import {
	MenuWrapper,
	Wrapper,
	TableWrapper,
	ShowMoreWrapper,
	NoData,
	LoaderWrapper,
} from './RegionalTable.styles';

const HEADER_HEIGHT = 30;
const ROW_HEIGHT = 24;

const agGridRowSelector = rowIndex =>
	`.ag-center-cols-container [row-id="${rowIndex}"]`;

export class RegionalTableView extends React.Component {
	state = {
		isMenuOpen: false,
		hoveredRowIndex: null,
	};
	gridApi = null;

	componentDidUpdate(prevProps) {
		const { isLoading } = this.props;

		if (isLoading !== prevProps.isLoading) {
			if (isLoading) {
				this.showLoadingOverlay();
			} else {
				this.hideLoadingOverlay();
			}
		}
	}

	onGridReady = params => {
		this.gridApi = params.api;
		params.api.sizeColumnsToFit();
	};

	handleSortChange = () => {
		const { onSortChanged } = this.props;

		if (!this.gridApi) {
			return;
		}

		onSortChanged(this.gridApi.getSortModel());
	};

	showLoadingOverlay = () => {
		if (!this.gridApi) {
			return;
		}

		this.gridApi.showLoadingOverlay();
	};

	hideLoadingOverlay = () => {
		if (!this.gridApi) {
			return;
		}

		this.gridApi.hideOverlay();
	};

	toggleContextualMenu = () => {
		const { isMenuOpen } = this.state;
		this.setState({ isMenuOpen: !isMenuOpen });
	};

	hideContextualTrigger = () => {
		const { isMenuOpen } = this.state;
		if (!isMenuOpen) {
			this.setState({ hoveredRowIndex: null });
		}
	};

	hideContextualMenu = () => {
		this.setState({ isMenuOpen: false });
	};

	getGridOptions = () => {
		const { data, columnDefs, pinnedBottomRowData } = this.props;
		const { isMenuOpen, hoveredRowIndex } = this.state;

		return {
			defaultColDef: {
				suppressMenu: true,
				// Prevent default sorting, as we handle sorting on server side
				comparator: () => 0,
			},
			pinnedBottomRowData,
			rowData: data,
			rowHeight: ROW_HEIGHT,
			headerHeight: HEADER_HEIGHT,
			columnDefs,
			suppressCopyRowsToClipboard: true,
			suppressScrollOnNewData: true,
			suppressRowHoverHighlight: true,
			suppressRowDrag: true,
			suppressRowClickSelection: true,
			suppressCellSelection: true,
			suppressCsvExport: true,
			suppressExcelExport: true,
			suppressContextMenu: true,
			suppressMultiSort: true,
			suppressNoRowsOverlay: true,
			domLayout: 'autoHeight',
			onGridReady: this.onGridReady,
			onCellMouseOver: params => {
				const row = document.querySelectorAll(
					agGridRowSelector(params.rowIndex)
				)[0];
				if (row && params.rowIndex !== hoveredRowIndex && !isMenuOpen) {
					this.setState({ hoveredRowIndex: params.rowIndex });
				}
			},
			onSortChanged: this.handleSortChange,
		};
	};

	render() {
		const {
			data,
			totalCount,
			loadMore,
			isLoading,
			showCategorisationModal,
			onViewTransactionsClick,
			onTopVendorsClick,
			onChangeStatus,
			onMoveToBin,
			permissions,
			pinnedBottomRowData,
			apiParams,
		} = this.props;
		const { isMenuOpen, hoveredRowIndex } = this.state;
		const hasWritePermissions =
			permissions && permissions.includes(PERMISSIONS.TransactionsWrite);

		return (
			<Wrapper onMouseLeave={this.hideContextualTrigger}>
				{isLoading && data.length === 0 ? (
					<LoaderWrapper>
						<ApLoaderDefault loaderText={() => <ApLoaderText />} />
					</LoaderWrapper>
				) : data.length === 0 &&
				(!pinnedBottomRowData || pinnedBottomRowData.length === 0) ? (
					<NoData>No data available</NoData>
				) : (
					<React.Fragment>
						<TableWrapper>
							{data.map((row, index) => (
								<MenuWrapper
									top={HEADER_HEIGHT + index * ROW_HEIGHT}
									key={index}
								>
									{hoveredRowIndex === index && (
										<ContextualMenu
											onViewTransactions={() => {
												onViewTransactionsClick(row);
											}}
											onChangeCategorisation={
												showCategorisationModal && !apiParams.getTailSpendOnly
													? () => {
															showCategorisationModal(row);
													  }
													: null
											}
											onShowTopVendors={() => {
												onTopVendorsClick(row);
											}}
											hasWritePermissions={hasWritePermissions}
											onChangeStatus={
												!apiParams.getTailSpendOnly
													? () => onChangeStatus(row)
													: null
											}
											toggleMenu={this.toggleContextualMenu}
											moveToBin={
												!apiParams.getTailSpendOnly
													? () => onMoveToBin(row)
													: null
											}
											isOpen={hoveredRowIndex === index && isMenuOpen}
										/>
									)}
								</MenuWrapper>
							))}
							<div className="ag-theme-balham">
								<AgGridReact {...this.getGridOptions()} />
							</div>
						</TableWrapper>
						<ShowMoreWrapper>
							{data.length}/{totalCount}
							{totalCount !== data.length && (
								<ApButtonSecondary onClick={loadMore} small>
									Show 10 more items
								</ApButtonSecondary>
							)}
						</ShowMoreWrapper>
					</React.Fragment>
				)}
			</Wrapper>
		);
	}
}

export const RegionalTable = props => (
	<PermissionsContext.Consumer>
		{({ permissions }) => (
			<RegionalTableView {...props} permissions={permissions || []} />
		)}
	</PermissionsContext.Consumer>
);
